import s from './profile-form-modal.module.scss'
import { useState } from 'react'
import classNames from 'classnames'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { format } from 'date-fns'
import { motion } from 'framer-motion'
import { Button } from '@shared/ui/button/button'
import { Input } from '@shared/ui/input/input'
import { Modal } from '@shared/ui/modal/modal'
import { CalendarInput } from '@shared/ui/calendar-input/calendar-input'
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji'
import { useAllInstruments } from '@shared/api/useAllInstruments'
import { useCreateProfile } from '@entities/profile/api/useCreateProfile'
import { useUpdateFamilyProfile } from '@entities/profile/api/useUpdateFamilyProfile'
import { getCDNPath } from '@utils/lib/getCDNpath'
import { getAvatarUrl } from '@utils/lib/getAvatarUrl'
import { flattenInstruments } from '@utils/lib/instruments'

// Пресетные аватары для детских профилей
const PRESET_AVATARS = [
	'images/avatars/avatar-sax.webp',
	'images/avatars/avatar-cello.webp',
	'images/avatars/avatar-violin-1.webp',
	'images/avatars/avatar-flute.webp',
	'images/avatars/avatar-violin-2.webp',
].map(getCDNPath)

// Форма создания/редактирования детского профиля.
// Поля профиля: имя, дата рождения, инструменты (остальное — на уровне аккаунта).
// Ключ по profile?.id (на странице) переводит форму в начальное состояние при смене профиля.
const ProfileForm = ({ close, profile }) => {
	const { data: instrumentGroups = [] } = useAllInstruments()
	const instruments = flattenInstruments(instrumentGroups)
	const createProfile = useCreateProfile()
	const updateProfile = useUpdateFamilyProfile()

	const methods = useForm({
		mode: 'onTouched',
		defaultValues: {
			name: profile?.name ?? '',
			bday: profile?.bday ?? '',
		},
	})

	const [selectedInstruments, setSelectedInstruments] = useState(
		profile?.instruments?.map((instrument) => instrument.id) ?? []
	)
	const [selectedAvatar, setSelectedAvatar] = useState(() =>
		profile?.avatar ? getAvatarUrl(profile.avatar) : PRESET_AVATARS[0]
	)

	const name = methods.watch('name')
	const bday = methods.watch('bday')
	const isValid = name?.trim().length >= 2 && !!bday
	const isPending = createProfile.isPending || updateProfile.isPending

	const toggleInstrument = (id) => {
		setSelectedInstruments((prev) =>
			prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
		)
	}

	const submit = methods.handleSubmit((data) => {
		const payload = {
			name: data.name.trim(),
			bday: data.bday,
			instruments: selectedInstruments,
			avatar: selectedAvatar,
		}

		const request = profile
			? updateProfile.mutateAsync({ id: profile.id, ...payload })
			: createProfile.mutateAsync(payload)

		request.then(close)
	})

	return (
		<FormProvider {...methods}>
			<motion.form
				className={s.card}
				onSubmit={submit}
				initial={{ opacity: 0, y: 8 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.25, ease: 'easeInOut' }}
			>
				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Аватар</h3>
					<div className={s.avatars}>
						{PRESET_AVATARS.map((avatar) => (
							<button
								key={avatar}
								type="button"
								className={classNames(s.avatarOption, selectedAvatar === avatar && s.avatarActive)}
								onClick={() => setSelectedAvatar(avatar)}
								aria-label="Выбрать аватар"
							>
								<img src={avatar} alt="" />
							</button>
						))}
					</div>
				</div>
				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Данные ребёнка</h3>
					<div className={s.fields}>
						<Input
							name="name"
							placeholder="Иван"
							className={s.input}
							validation={{
								required: 'Укажите имя',
								minLength: { value: 2, message: 'Минимум 2 символа' },
							}}
						/>
						<Controller
							name="bday"
							control={methods.control}
							rules={{ required: 'Укажите дату рождения' }}
							render={({ field }) => (
								<CalendarInput
									placeholder="ДД.ММ.ГГГГ"
									value={field.value}
									onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
								/>
							)}
						/>
					</div>
				</div>
				<div className={s.sectionCard}>
					<h3 className={s.sectionTitle}>Инструменты</h3>
					<div className={s.instruments}>
						{instruments.map((instrument) => (
							<button
								key={instrument.id}
								type="button"
								className={classNames(
									s.instrumentChip,
									selectedInstruments.includes(instrument.id) && s.instrumentActive
								)}
								onClick={() => toggleInstrument(instrument.id)}
							>
								<TextWithEmoji text={instrument.name} />
							</button>
						))}
					</div>
				</div>
				<div className={s.footer}>
					<Button
						label={profile ? 'Сохранить' : 'Создать профиль'}
						className={s.submitBtn}
						disabled={!isValid}
						fetching={isPending}
					/>
				</div>
			</motion.form>
		</FormProvider>
	)
}

export const ProfileFormModal = ({ isOpen, close, profile }) => {
	const modalStyle = {
		content: {
			width: 'calc(100% - 40px)',
			maxWidth: '420px',
			maxHeight: 'calc(100svh - 40px)',
			padding: '20px',
			overflow: 'auto',
		},
	}

	return (
		<Modal isOpen={isOpen} close={close} title={profile ? 'Редактировать профиль' : 'Добавить ребёнка'} style={modalStyle}>
			<ProfileForm key={profile?.id ?? 'new'} close={close} profile={profile} />
		</Modal>
	)
}
